import { Component, EventEmitter, Input, OnInit, Output, signal, Type, ViewChild } from '@angular/core';
import { DELETE_LABEL, PER_PAGE } from '@microsec/constants';
import { BaseComponent } from '@ids-components';
import { ActionMenuItem } from '@microsec/models';
import { ANALYZER_TYPES, IP_RULE_TYPES, NONIP_BUILT_IN_RULES, YARA_BUILT_IN_RULES } from '@ids-constants';

import { AnomalyAnalyzerService } from '@ids-services';
import { IpSuppressedRuleFormComponent } from './ip-suppressed-rule-form/ip-suppressed-rule-form.component';
import { IpWhitelistFormComponent } from './ip-whitelist-form/ip-whitelist-form.component';
import { RulesetRuleTypeComponent } from './ruleset-rule-type/ruleset-rule-type.component';
import { CommonModule } from '@angular/common';
import { CardComponent, CommonTableComponent } from '@microsec/components';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
@Component({
  selector: 'app-analyzer-rule-config-field',
  templateUrl: './analyzer-rule-config-field.component.html',
  styleUrls: ['./analyzer-rule-config-field.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    ToggleSwitchModule,
    AccordionModule,
    CommonTableComponent,
    CardComponent,
    RulesetRuleTypeComponent,
  ],
})
export class AnalyzerRuleConfigFieldComponent extends BaseComponent implements OnInit {
  rulesetCols = [
    { field: 'enabled', header: 'Status', width: 10 },
    { field: 'name', header: 'Ruleset', width: 15 },
    { field: 'description', header: 'Description', width: 15 },
  ];

  customRuleCols = [
    { field: 'enabled', header: 'Status', width: 8 },
    { field: 'name', header: 'Name', width: 8 },
    { field: 'description', header: 'Description', width: 12 },
    { field: 'rule', header: 'Rule', width: 20 },
  ];

  //---------- For IP rule-based Analyzer -------------
  suppressedRuleCols = [
    { field: 'enabled', header: 'Status', width: 10 },
    { field: 'rule_gid', header: 'Rule GID', width: 10 },
    { field: 'rule_sid', header: 'Rule SID', width: 10 },
    { field: 'comment', header: 'Comment', width: 15 },
  ];

  //---------- For IP rule-based Analyzer -------------
  whitelistCols = [
    { field: 'enabled', header: 'Status', width: 10 },
    { field: 'ip', header: 'IP', width: 10 },
    { field: 'comment', header: 'Comment', width: 15 },
  ];

  customRuleRows = PER_PAGE;

  customRuleFirstIndex = 0;

  enabledRuleset = true;

  enabledCustomRuleset = true;

  // rulesets: any[] = [];
  displaySuppressedRules: any[] = [];
  displayWhitelists: any[] = [];
  _whitelists: any[] = [];
  _suppressedRules: any[] = [];

  @Input() set suppressedRules(value: any) {
    this.displaySuppressedRules = value;
    this._suppressedRules = value;
  }

  @Input() set whitelists(value: any) {
    this.displayWhitelists = value;
    this._whitelists = value;
  }

  customRules: any[] = [];

  customSuppressedActionsItems = signal<ActionMenuItem[]>([]);

  customWhitelistActionsItems = signal<ActionMenuItem[]>([]);

  @Output() setLoadingEvent: EventEmitter<any> = new EventEmitter<any>();

  @Input() analyzer: any = null;

  @Input() type = '';

  @Input() ruleTypes: any[] = [];

  @ViewChild('rulesetRuleTypeComponent') rulesetsComponent!: RulesetRuleTypeComponent;

  ANALYZER_TYPES = ANALYZER_TYPES;

  IP_RULE_TYPES = IP_RULE_TYPES;

  constructor(public anomalyAnalyzerSrv: AnomalyAnalyzerService) {
    super();
  }

  async ngOnInit() {
    await this.prepareConfigs();
    this.customSuppressedActionsItems.set(this.getCustomActionMenus(IP_RULE_TYPES.SUPPRESSED));
    this.customWhitelistActionsItems.set(this.getCustomActionMenus(IP_RULE_TYPES.WHITELIST));
    if (!!this.analyzer) {
      this.populateForm();
    } else {
      switch (this.type) {
        case ANALYZER_TYPES.RULE_BASED: {
          break;
        }
        case ANALYZER_TYPES.RULE_BASED_NONIP: {
          this.ruleTypes = [...NONIP_BUILT_IN_RULES.map((rule) => ({ ...rule, enabled: true }))];
          break;
        }
        case ANALYZER_TYPES.RULE_BASED_YARA: {
          this.ruleTypes = [...YARA_BUILT_IN_RULES.map((rule) => ({ ...rule, enabled: true }))];
          break;
        }
        default: {
          break;
        }
      }
    }
  }

  openForm(type: 'rules_custom' | 'rules_suppressed' | 'whitelist', data?: any, index?: number) {
    let form: Type<any>;
    let title: string;
    switch (type) {
      case IP_RULE_TYPES.SUPPRESSED: {
        form = IpSuppressedRuleFormComponent;
        title = 'Suppressed Rule';
        break;
      }
      default: {
        form = IpWhitelistFormComponent;
        title = 'IP Whitelist';
        break;
      }
    }
    const dialog = this.dialogSrv.open(form, {
      data: !data ? { analyzerId: this.analyzer?.id } : { analyzerId: this.analyzer?.id, data: data },
      header: `${!data ? 'Create' : 'Edit'} ${title}`,
      width: '800px',
      height: 'min-content',
      closeOnEscape: true,
    });
    dialog.onClose.subscribe((rs) => {
      if (!!rs && rs.type) {
        const isNew = !rs.id && index === undefined;
        const isEdit = typeof index === 'number' || typeof index === 'string';
        const ruleMap = {
          [IP_RULE_TYPES.SUPPRESSED]: this.displaySuppressedRules,
          [IP_RULE_TYPES.WHITELIST]: this.displayWhitelists,
        };
        const targetList = ruleMap[rs.type];
        if (targetList) {
          if (isNew) {
            targetList.push(rs);
          } else if (isEdit) {
            targetList[index] = rs;
          }
        }
        // remove the type from payload i.e rules_suppressed or whitelist
        delete rs.type;
      }
    });
  }

  openDeleteConfirmation(type: string, data: any, index: any) {
    let label = '';
    let rules: any[] = [];
    let customContent = '';
    switch (type) {
      case IP_RULE_TYPES.SUPPRESSED: {
        rules = this.displaySuppressedRules;
        customContent = 'One of the Suppressed Rules';
        label = 'Suppressed Rule';
        break;
      }
      case IP_RULE_TYPES.WHITELIST: {
        rules = this.displayWhitelists;
        customContent = 'One of the Whitelist Rules';
        label = 'Whitelist Rule';
        break;
      }
      default: {
        break;
      }
    }
    this.confirm({
      action: DELETE_LABEL,
      objectName: 'Analyzer',
      customContent: `<ul><li>${customContent}</li></ul>`,
      next: () => {
        this.customRules = this.customRules.filter((_, i) => i !== index);
        switch (type) {
          case IP_RULE_TYPES.SUPPRESSED: {
            this.displaySuppressedRules = rules.filter((_, i) => i !== index);
            break;
          }
          case IP_RULE_TYPES.WHITELIST: {
            this.displayWhitelists = rules.filter((_, i) => i !== index);
            break;
          }
          default: {
            break;
          }
        }
        this.showSuccessMessage(`Deleted ${label} successfully`);
      },
    });
  }

  getCustomActionMenus(type: string) {
    return [
      {
        label: 'Edit',
        icon: 'fas fa-edit',
        command: ({ rowData, index }) => {
          type === IP_RULE_TYPES.SUPPRESSED ? this.openForm('rules_suppressed', rowData, index) : this.openForm('whitelist', rowData, index);
        },
      },
      {
        label: 'Delete',
        icon: 'fas fa-trash',
        command: ({ rowData, index }) => this.openDeleteConfirmation(type, rowData, index),
      },
    ] as ActionMenuItem[];
  }

  populateForm() {
    const data = this.analyzer.rule_types;
    this.ruleTypes = data;
  }
}
